import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import Pagination from "react-js-pagination";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const emptyForm = (branchId, physioId = "") => ({
  response_time: moment().format("YYYY-MM-DDTHH:mm"),
  client_id: "",
  name: "",
  age: "",
  most_helpful_part: "",
  confidence_level: "",
  unclear_or_confusing: "",
  change_one_thing: "",
  suggestions: "",
  branch_id: branchId,
  physio_id: physioId,
});

export default function PhysioClientResponses() {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;
  const isPhysioUser = role === "15";
  const defaultPhysioId = isPhysioUser ? UId : "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm(branch_id, defaultPhysioId));

  const [search, setSearch] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    fetchRows(1, limit);
    fetchPhysios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  const fetchRows = async (page = 1, lim = limit, override = {}) => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const res = await UserServices.getPhysioClientResponses({
        branch_id,
        limit: lim,
        page,
        physio_id: isPhysioUser ? UId : override.physio_id || undefined,
        search: (override.search ?? search) || undefined,
        start_date: (override.start_date ?? filterStartDate) || undefined,
        end_date: (override.end_date ?? filterEndDate) || undefined,
      });
      if (res.status === 200 && res.data.status) {
        setRows(res.data.data || []);
        if (res.data.pagination) {
          setCurrentPage(res.data.pagination.current_page || 1);
          setTotalResults(res.data.pagination.total_record || 0);
        } else {
          setCurrentPage(1);
          setTotalResults((res.data.data || []).length);
        }
      }
    } catch (err) {
      console.log(err);
      setRows([]);
    }
    setLoading(false);
  };

  const fetchPhysios = async () => {
    try {
      const res = await UserServices.getPhysioUsers(branch_id);
      if (res.status === 200 && res.data.status) {
        setPhysios(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openAdd = () => {
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelected(row);
    setFormData({
      response_time: row.response_time
        ? moment(row.response_time).format("YYYY-MM-DDTHH:mm")
        : moment().format("YYYY-MM-DDTHH:mm"),
      client_id: row.client_id || "",
      name: row.name || "",
      age: row.age || "",
      most_helpful_part: row.most_helpful_part || "",
      confidence_level: row.confidence_level || "",
      unclear_or_confusing: row.unclear_or_confusing || "",
      change_one_thing: row.change_one_thing || "",
      suggestions: row.suggestions || "",
      branch_id: row.branch_id || branch_id,
      physio_id: row.physio_id || defaultPhysioId,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setSaving(false);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.name.trim()) {
      alert("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        age: formData.age === "" ? null : Number(formData.age),
        response_time: formData.response_time
          ? moment(formData.response_time).format("YYYY-MM-DD HH:mm:ss")
          : null,
        physio_id: isPhysioUser ? UId : formData.physio_id || null,
      };
      if (editMode && selected) {
        await UserServices.updatePhysioClientResponse(selected.id, payload);
      } else {
        await UserServices.createPhysioClientResponse(payload);
      }
      closeModal();
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Failed to save response");
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this response?")) return;
    try {
      await UserServices.deletePhysioClientResponse(id);
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert("Failed to delete response");
    }
  };

  const handleExcel = async () => {
    try {
      const res = await UserServices.exportPhysioClientResponses({
        branch_id,
        physio_id: isPhysioUser ? UId : undefined,
        search: search || undefined,
        start_date: filterStartDate || undefined,
        end_date: filterEndDate || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Physio_Client_Responses_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Failed to export Excel");
    }
  };

  const handlePdf = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Physio Client Responses", 14, 16);
      doc.autoTable({
        startY: 22,
        head: [[
          "S#",
          "Timestamp",
          "Name",
          "Age",
          "Most Helpful",
          "Confidence",
          "Unclear/Rushed",
          "Change One Thing",
          "Suggestions",
        ]],
        body: rows.map((r, i) => [
          (currentPage - 1) * limit + i + 1,
          r.response_time ? moment(r.response_time).format("DD/MM/YY HH:mm") : "",
          r.name || "",
          r.age || "",
          r.most_helpful_part || "",
          r.confidence_level || "",
          r.unclear_or_confusing || "",
          r.change_one_thing || "",
          r.suggestions || "",
        ]),
        headStyles: { fillColor: [46, 125, 50], textColor: 255 },
        styles: { fontSize: 7 },
      });
      doc.save(`Physio_Client_Responses_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="page-heading d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="bi bi-chat-square-heart me-2"></i>
            Client Responses
          </h3>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-circle me-1"></i>Add Response
          </button>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name / helpful part / suggestions"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-4 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => fetchRows(1, limit)}>Search</button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setFilterStartDate("");
                    setFilterEndDate("");
                    fetchRows(1, limit, { search: "", start_date: "", end_date: "" });
                  }}
                >
                  Clear
                </button>
                <button className="btn btn-success" onClick={handleExcel}>Excel</button>
                <button className="btn btn-danger" onClick={handlePdf}>PDF</button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No responses found.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Timestamp</th>
                        <th>Name</th>
                        <th>Age</th>
                        <th>Most Helpful Part</th>
                        <th>Confidence</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id}>
                          <td>{(currentPage - 1) * limit + i + 1}</td>
                          <td>{r.response_time ? moment(r.response_time).format("DD/MM/YY HH:mm") : "—"}</td>
                          <td>{r.name || "—"}</td>
                          <td>{r.age || "—"}</td>
                          <td style={{ maxWidth: 240 }}>{r.most_helpful_part || "—"}</td>
                          <td>{r.confidence_level || "—"}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => openEdit(r)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => handleDelete(r.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalResults > limit ? (
                  <div className="d-flex justify-content-center">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => {
                        setCurrentPage(p);
                        fetchRows(p, limit);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <Modal show={showModal} onHide={closeModal} size="lg" backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? "Edit Response" : "Add Response"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={submit}>
            <Modal.Body>
              <div className="row g-3">
                {!isPhysioUser ? (
                  <div className="col-md-6">
                    <label className="form-label">Physiotherapist</label>
                    <select
                      className="form-select"
                      name="physio_id"
                      value={formData.physio_id}
                      onChange={handleInput}
                      required
                    >
                      <option value="">Select physiotherapist</option>
                      {physios.map((p) => (
                        <option key={p.id} value={p.id}>
                          {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                <div className="col-md-6">
                  <label className="form-label">Timestamp</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="response_time"
                    value={formData.response_time}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-8">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Age</label>
                  <input
                    type="number"
                    min={1}
                    max={120}
                    className="form-control"
                    name="age"
                    value={formData.age}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    What was the MOST helpful part of your consultation today?
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="most_helpful_part"
                    value={formData.most_helpful_part}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    After the consultation, how confident do you feel about managing your condition?
                  </label>
                  <input
                    className="form-control"
                    name="confidence_level"
                    value={formData.confidence_level}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Did anything feel unclear, rushed, or confusing during your visit?
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="unclear_or_confusing"
                    value={formData.unclear_or_confusing}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    If you could change ONE thing about your consultation, what would it be?
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="change_one_thing"
                    value={formData.change_one_thing}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Suggestions</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="suggestions"
                    value={formData.suggestions}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  );
}
